import React, { useEffect, useRef } from "react";
import { Offcanvas } from "react-bootstrap";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useDispatch, useSelector } from "react-redux";
import Input from "../../nodes/Logic/Inputs/Input";
import timezones from "../../nodes/Logic/timezones";
import ExtractOptions from "../../nodes/Logic/Inputs/ExtractOptions";
import UsedInLink from "../UsedInLink";
import JsonCollapse from "../JsonCollapse";
import {
  addNewInputArgument,
  selectInputArgument,
  selectInputArguments,
  selectProjectTimezone,
  selectVariableGroups,
  setInputArgument,
} from "../../../store/slices/projectSlice";
import { faEdit } from "@fortawesome/free-solid-svg-icons";
import { useGetContactFieldsQuery } from "../../../store/slices/contactsSlice";
import {
  closeModal,
  selectCurrentModal,
  selectModalData,
  setCurrentModal,
} from "../../../store/slices/modalsSlice";
import { useGetApiDataCollectionsQuery } from "../../../store/slices/settingsSlice";
import { useGetReportingTemplatesQuery } from "../../../store/slices/reportingSlice";
import { pageProps } from "../../../helpers/pageProps";
import VisibilityInputs from "./VisibilityInputs";
import InputArgumentField from "../sidebar/ProjectStartForm/InputArgumentField";
import {
  getButtonOptionIcon,
  getPrunedButtonOptionIcons,
} from "../inputArgumentOptionHelpers";
import {
  formatSelectOptionIconOptionLabel,
  getSelectOptionIconValueLabels,
  loadSelectOptionIconOptions,
} from "../selectOptionIconHelpers";

const FILE_TYPE_OPTIONS = [
  { value: "json", label: "JSON" },
  { value: "csv", label: "CSV" },
  { value: "pdf", label: "PDF" },
  { value: "excel", label: "Excel" },
  { value: "txt", label: "Text" },
  { value: "doc", label: "Document" },
];

const FILE_POLICY_OPTIONS = [
  { value: "either", label: "Either" },
  { value: "base64", label: "Base64" },
  { value: "multipart", label: "Multipart" },
];

const FILE_SOURCE_OPTIONS = [
  { value: "direct", label: "Direct upload" },
  { value: "library", label: "File library" },
];

export default function InputArgument() {
  const show = useSelector(selectCurrentModal) === "inputArgument";

  const { inputArgumentUUID, groupUuid } = useSelector(selectModalData) || {};
  const inputArguments = useSelector(selectInputArguments);
  const {
    data: contactFields,
    isFetching,
    isLoading,
  } = useGetContactFieldsQuery();

  const editedVariable = useSelector(selectInputArgument(inputArgumentUUID));
  const projectTimezone = useSelector(selectProjectTimezone);
  const isProjectSolution = useSelector(
    (state) => state.project.IsSolution || !!state.project.SolutionUuid
  );

  const dispatch = useDispatch();

  const setEditedVariable = (fn) => {
    if (typeof fn === "function") {
      dispatch(setInputArgument(fn(editedVariable)));
    } else {
      dispatch(setInputArgument(fn));
    }
  };

  const labelRef = useRef();

  const handleClose = () => {
    if (editedVariable.Label && editedVariable.Format) {
      dispatch(closeModal());
    } else {
      toast.error("Label or format is missing.");
    }
  };

  useEffect(() => {
    // set edited variable when the window shows
    if (inputArgumentUUID) {
      if (inputArgumentUUID === "create_new") {
        const newIAUUID = uuidv4();
        dispatch(
          addNewInputArgument({ UUID: newIAUUID, GroupUuid: groupUuid })
        );
        dispatch(
          setCurrentModal({
            modal: "inputArgument",
            data: { inputArgumentUUID: newIAUUID },
          })
        );
      }
    }
  }, [inputArgumentUUID]);

  const { data: { collections: apiDataCollections } = { collections: [] } } =
    useGetApiDataCollectionsQuery(
      {},
      {
        skip: !show || !editedVariable || editedVariable.Format !== "json",
      }
    );

  const { data: { data: reportingTemplates } = { data: [] } } =
    useGetReportingTemplatesQuery(
      { pagination: { page: 0, perPage: 100 } },
      {
        skip:
          !show || !editedVariable || editedVariable.Format !== "data-entry",
      }
    );

  const variableGroups = useSelector(selectVariableGroups);
  const usesButtonOptions = editedVariable?.DataStructure === "Buttons";
  const defaultValueValues = ["contact", "group"].includes(
    editedVariable?.Format
  )
    ? editedVariable?.Default
      ? [editedVariable.Default]
      : []
    : [editedVariable?.Default ?? ""];

  return (
    editedVariable && (
      <div>
        <Offcanvas
          show={show}
          onHide={handleClose}
          onEntered={() => labelRef.current.select()}
          className="input-argument-offcanvas z-3000"
          backdropClassName="z-2999"
        >
          <Offcanvas.Header closeButton>
            <Offcanvas.Title>
              <FontAwesomeIcon icon={faEdit} /> Edit variable
            </Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body>
            <div>
              <div className="mb-3">
                <label htmlFor="inputVariableLabel" className="form-label">
                  Label
                </label>
                <input
                  ref={labelRef}
                  type="text"
                  placeholder="Variable label"
                  className="form-control"
                  id="inputVariableLabel"
                  value={editedVariable?.Label ?? ""}
                  onChange={(e) =>
                    setEditedVariable((prev) => ({
                      ...prev,
                      Label: e.target.value,
                    }))
                  }
                />
                {editedVariable && !editedVariable?.Label && (
                  <span className="text-danger">Need a label!</span>
                )}
              </div>

              <div className="mb-3">
                <label htmlFor="inputVariableFormat" className="form-label">
                  Format
                </label>
                <select
                  className="form-select"
                  id="inputVariableFormat"
                  value={editedVariable?.Format ?? ""}
                  onChange={(e) =>
                    setEditedVariable((prev) => ({
                      ...prev,
                      Format: e.target.value,
                      DateTimeFormat:
                        (e.target.value === "dateTime" && "YYYY-MM-DD HH:mm") ||
                        (e.target.value === "date" && "YYYY-MM-DD") ||
                        (e.target.value === "time" && "HH:mm") ||
                        "",
                      ...(e.target.value === "configuration"
                        ? {
                            DataStructure:
                              Object.keys(
                                pageProps.configurations || {}
                              )?.[0] || "",
                            Default: "",
                          }
                        : { DataStructure: "", Default: "" }),
                      ...(e.target.value === "editor"
                        ? {
                            IsSelect: false,
                            IsMultiple: false,
                            IsReadonly: true,
                            IsParameter: true,
                            InEditor: false,
                          }
                        : {}),
                    }))
                  }
                >
                  <option value="" disabled>
                    Select a format
                  </option>
                  <option value="short-text">short-text</option>
                  <option value="editor">editor</option>
                  <option value="number">number</option>
                  <option value="json">json</option>
                  <option value="business-hours">business hours</option>
                  <option value="geo-json">geoJSON</option>
                  <option value="email">email</option>
                  <option value="sms">sms</option>
                  <option value="voice">voice</option>
                  <option value="date">date</option>
                  <option value="time">time</option>
                  <option value="dateTime">date time</option>
                  <option value="contact">contact</option>
                  <option value="group">contact group</option>
                  <option value="asset">asset</option>
                  <option value="event">event</option>
                  <option value="ticket">ticket</option>
                  <option value="data-entry">reporting data entry</option>
                  <option value="binary">file</option>
                  <option value="configuration">configuration</option>
                </select>
              </div>

              {editedVariable?.Format === "binary" && (
                <div className="mb-3">
                  <label className="form-label">Allowed file types</label>
                  <Input
                    value={editedVariable?.FileAllowedTypes || []}
                    onChange={(newValue) =>
                      setEditedVariable((prev) => ({
                        ...prev,
                        FileAllowedTypes: newValue,
                      }))
                    }
                    options={FILE_TYPE_OPTIONS}
                    inputType="select"
                    isMulti
                    isClearable
                    placeholder="All file types"
                  />
                  <div className="row g-2 mt-2">
                    <div className="col-12 col-md-6">
                      <label htmlFor="fileMaxBytes" className="form-label">
                        Max size (MB)
                      </label>
                      <input
                        id="fileMaxBytes"
                        type="number"
                        min="0"
                        step="0.1"
                        className="form-control"
                        value={
                          editedVariable?.FileMaxBytes
                            ? Math.round(
                                (editedVariable.FileMaxBytes / (1024 * 1024)) * 10
                              ) / 10
                            : ""
                        }
                        onChange={(e) =>
                          setEditedVariable((prev) => ({
                            ...prev,
                            FileMaxBytes: e.target.value
                              ? Math.round(Number(e.target.value) * 1024 * 1024)
                              : 0,
                          }))
                        }
                      />
                    </div>
                    <div className="col-12 col-md-6">
                      <label className="form-label">Upload mode</label>
                      <Input
                        value={[editedVariable?.FileUploadMode || "either"]}
                        onChange={([value]) =>
                          setEditedVariable((prev) => ({
                            ...prev,
                            FileUploadMode: value,
                          }))
                        }
                        options={FILE_POLICY_OPTIONS}
                        inputType="select"
                        isValidNewOption={() => false}
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Source</label>
                      <Input
                        value={[editedVariable?.FileSource || "direct"]}
                        onChange={([value]) =>
                          setEditedVariable((prev) => ({
                            ...prev,
                            FileSource: value,
                          }))
                        }
                        options={FILE_SOURCE_OPTIONS}
                        inputType="select"
                        isValidNewOption={() => false}
                      />
                    </div>
                  </div>
                </div>
              )}

              {editedVariable?.Format === "json" && (
                <div className="mb-3 d-flex gap-3">
                  <div className="flex-grow-1">
                    <label className="form-label">Data structure</label>
                    <Input
                      value={[editedVariable?.DataStructure || ""]}
                      onChange={(newValue) =>
                        setEditedVariable((prev) => ({
                          ...prev,
                          DataStructure: newValue[0],
                        }))
                      }
                      options={
                        apiDataCollections
                          ? [
                              { value: "", label: "No structure" },
                              {
                                value: "weather-alert",
                                label: "Weather Alert (System Data Structure)",
                              },
                              ...apiDataCollections.map((adc) => ({
                                value: adc.Uuid,
                                label: adc.Label,
                              })),
                            ]
                          : [{ value: "", label: "No structure" }]
                      }
                      inputType="select"
                      isValidNewOption={() => false}
                      placeholder="Select data structure"
                    />
                  </div>
                </div>
              )}

              {editedVariable?.Format === "geo-json" && (
                <div className="mb-3 d-flex gap-3">
                  <div className="flex-grow-1">
                    <label className="form-label">Geo JSON types</label>
                    <Input
                      value={editedVariable?.GeoJsonTypes || []}
                      onChange={(newValue) =>
                        setEditedVariable((prev) => ({
                          ...prev,
                          GeoJsonTypes: newValue,
                        }))
                      }
                      options={[
                        { value: "polygon", label: "Polygon" },
                        { value: "rectangle", label: "Rectangle" },
                        { value: "marker", label: "Marker" },
                      ]}
                      inputType="select"
                      isMulti
                      isClearable
                      placeholder="Select Geo JSON types - Empty means all types"
                    />
                  </div>
                </div>
              )}

              {editedVariable?.Format === "configuration" && (
                <div className="mb-3 d-flex gap-3">
                  <div className="flex-grow-1">
                    <label className="form-label">Data structure</label>
                    <Input
                      value={[editedVariable?.DataStructure || ""]}
                      onChange={(newValue) =>
                        setEditedVariable((prev) => ({
                          ...prev,
                          DataStructure: newValue[0],
                        }))
                      }
                      options={Object.entries(
                        pageProps.configurations ?? {}
                      ).map(([value]) => ({
                        value,
                        label: value,
                      }))}
                      inputType="select"
                      isValidNewOption={() => false}
                      placeholder="Select data structure"
                    />
                  </div>
                </div>
              )}

              {editedVariable?.Format === "data-entry" && (
                <div className="mb-3 d-flex gap-3">
                  <div className="flex-grow-1">
                    <label className="form-label">Reporting template</label>
                    <Input
                      value={[editedVariable?.DataStructure || ""]}
                      onChange={(newValue) =>
                        setEditedVariable((prev) => ({
                          ...prev,
                          DataStructure: newValue[0],
                        }))
                      }
                      options={
                        reportingTemplates.length
                          ? reportingTemplates.map((template) => ({
                              value: template.uuid,
                              label: template.label,
                            }))
                          : [{ value: "", label: "No template available" }]
                      }
                      inputType="select"
                      isValidNewOption={() => false}
                      placeholder="Select reporting template"
                    />
                  </div>
                </div>
              )}

              {editedVariable?.Format === "number" && (
                <div className="mb-3 d-flex gap-3">
                  <div className="flex-grow-1">
                    <label htmlFor="numberMinValue" className="form-label">
                      Min
                    </label>
                    <input
                      // type="number"
                      placeholder="Variable min"
                      className="form-control"
                      id="numberMinValue"
                      value={editedVariable?.Min ?? ""}
                      type="number"
                      onChange={(e) => {
                        // if (isNaN(e.target.value)) return;
                        setEditedVariable((prev) => ({
                          ...prev,
                          Min: e.target.valueAsNumber,
                        }));
                      }}
                    />
                  </div>
                  <div className="flex-grow-1">
                    <label htmlFor="numberMaxValue" className="form-label">
                      Max
                    </label>
                    <input
                      placeholder="Variable max"
                      className="form-control"
                      id="numberMaxValue"
                      type="number"
                      value={editedVariable?.Max ?? ""}
                      onChange={(e) => {
                        // if (isNaN(e.target.value)) return;
                        setEditedVariable((prev) => ({
                          ...prev,
                          Max: e.target.valueAsNumber,
                        }));
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Is multiple */}
              <div className="mb-3 form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="inputVariableIsMultiple"
                  checked={editedVariable?.IsMultiple ?? false}
                  disabled={["editor"].includes(editedVariable?.Format)}
                  onChange={() =>
                    setEditedVariable((prev) => ({
                      ...prev,
                      IsMultiple: !prev.IsMultiple,
                    }))
                  }
                />
                <label
                  className="form-check-label"
                  htmlFor="inputVariableIsMultiple"
                >
                  Is multiple?
                </label>
              </div>
              <div className="mb-3 form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="inputVariableIsReadonly"
                  checked={editedVariable?.IsReadonly ?? false}
                  disabled={["editor"].includes(editedVariable?.Format)}
                  onChange={() =>
                    setEditedVariable((prev) => ({
                      ...prev,
                      IsReadonly: !prev.IsReadonly,
                    }))
                  }
                />
                <label
                  className="form-check-label"
                  htmlFor="inputVariableIsReadonly"
                >
                  Is readonly?
                </label>
              </div>

              <div className="mb-3 form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="inputVariableShowOnStartForm"
                  checked={editedVariable?.IsParameter ?? false}
                  disabled={["editor"].includes(editedVariable?.Format)}
                  onChange={() =>
                    setEditedVariable((prev) => ({
                      ...prev,
                      IsParameter: !prev.IsParameter,
                      ...(prev.IsParameter
                        ? {
                            IsRequiredManual: false,
                          }
                        : {}),
                    }))
                  }
                />
                <label
                  className="form-check-label"
                  htmlFor="inputVariableShowOnStartForm"
                >
                  Show on the start form?
                </label>
              </div>

              {isProjectSolution && editedVariable?.IsParameter && (
                <div className="mb-3">
                  <label htmlFor="inputVariableRole" className="form-label">
                    Solution role
                  </label>
                  <input
                    type="text"
                    placeholder="Solution role"
                    className="form-control"
                    id="inputVariableRole"
                    value={editedVariable?.Role ?? ""}
                    onChange={(e) =>
                      setEditedVariable((prev) => ({
                        ...prev,
                        Role: e.target.value,
                      }))
                    }
                  />
                </div>
              )}

              {editedVariable?.IsParameter && (
                <div className="mb-3">
                  <label
                    htmlFor="inputVariableDescription"
                    className="form-label"
                  >
                    Description
                  </label>
                  <textarea
                    placeholder="Description"
                    className="form-control"
                    id="inputVariableDescription"
                    value={editedVariable?.Description ?? ""}
                    maxLength={65000}
                    onChange={(e) =>
                      setEditedVariable((prev) => ({
                        ...prev,
                        Description: e.target.value,
                      }))
                    }
                  />
                </div>
              )}

              {isProjectSolution && editedVariable?.IsParameter && (
                <div className="mb-3 form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="inputVariableSolutionDoNotConfigure"
                    checked={editedVariable?.SolutionDoNotConfigure ?? false}
                    onChange={() =>
                      setEditedVariable((prev) => ({
                        ...prev,
                        SolutionDoNotConfigure: !prev.SolutionDoNotConfigure,
                      }))
                    }
                  />
                  <label
                    className="form-check-label"
                    htmlFor="inputVariableSolutionDoNotConfigure"
                  >
                    Do not configure in solution
                  </label>
                </div>
              )}

              <div className="mb-3 form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="inputVariableSensitive"
                  checked={editedVariable?.InEditor ?? false}
                  disabled={["editor"].includes(editedVariable?.Format)}
                  onChange={() =>
                    setEditedVariable((prev) => ({
                      ...prev,
                      InEditor: !prev.InEditor,
                    }))
                  }
                />
                <label
                  className="form-check-label"
                  htmlFor="inputVariableSensitive"
                >
                  Allow in editor tokens in the start form
                </label>
              </div>

              {["contact", "short-text"].includes(editedVariable?.Format) && (
                <div className="mb-3 form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="inputVariableAddInitiator"
                    checked={editedVariable?.AddInitiator ?? false}
                    onChange={() =>
                      setEditedVariable((prev) => ({
                        ...prev,
                        AddInitiator: !prev.AddInitiator,
                      }))
                    }
                  />
                  <label
                    className="form-check-label"
                    htmlFor="inputVariableAddInitiator"
                  >
                    {editedVariable?.Format === "short-text"
                      ? "Add initiating process GUID?"
                      : "Add initiator automatically at the process start?"}
                  </label>
                </div>
              )}

              {![
                "contact",
                "group",
                "asset",
                "event",
                "data-entry",
                "ticket",
                "json",
                "business-hours",
                "binary",
                "editor",
                "configuration",
              ].includes(editedVariable?.Format) && (
                <div className="mb-3 form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="inputVariableIsSelect"
                    checked={editedVariable?.IsSelect ?? false}
                    onChange={() =>
                      setEditedVariable((prev) => ({
                        ...prev,
                        IsSelect: !prev.IsSelect,
                        DataStructure: "",
                        SelectOptions: [],
                      }))
                    }
                  />
                  <label
                    className="form-check-label"
                    htmlFor="inputVariableIsSelect"
                  >
                    Is selectable?
                  </label>
                </div>
              )}

              {editedVariable?.Format === "short-text" && (
                // editedVariable?.IsSelect &&

                <div className="mb-3 d-flex gap-3">
                  <div className="flex-grow-1">
                    <label className="form-label">Select input type</label>
                    <Input
                      value={[editedVariable?.DataStructure || ""]}
                      onChange={(newValue) => {
                        if (newValue[0] === "yesNo") {
                          setEditedVariable((prev) => ({
                            ...prev,
                            DataStructure: "Buttons",
                            SelectOptions: ["yes", "no"],
                            SelectOptionsIcons: {
                              yes: "boom:tick.svg2",
                              no: "boom:close.svg2",
                            },
                          }));
                          return;
                        }
                        if (newValue[0] === "comms") {
                          setEditedVariable((prev) => ({
                            ...prev,
                            DataStructure: "Buttons",
                            SelectOptions: ["sms", "email", "voice"],
                            SelectOptionsIcons: {
                              sms: "boom:sms_text.svg2",
                              email: "boom:icon_email-01.svg2",
                              voice: "boom:icon_voice_tel-01.svg2",
                            },
                          }));
                          return;
                        }
                        if (newValue[0] === "1to5") {
                          setEditedVariable((prev) => ({
                            ...prev,
                            DataStructure: "Buttons",
                            SelectOptions: ["1", "2", "3", "4", "5"],
                            SelectOptionsIcons: {},
                          }));
                          return;
                        }
                        setEditedVariable((prev) => ({
                          ...prev,
                          DataStructure: newValue[0],
                        }));
                      }}
                      options={
                        editedVariable?.IsSelect
                          ? [
                              { value: "", label: "Dropdown" },
                              { value: "Buttons", label: "Buttons" },
                              {
                                value: "yesNo",
                                label: "Buttons with preconfigured Yes/No",
                              },
                              {
                                value: "comms",
                                label: "Buttons with preconfigured comms",
                              },
                              {
                                value: "1to5",
                                label: "Buttons with preconfigured 1...5",
                              },
                            ]
                          : [
                              { value: "", label: "Text" },
                              { value: "Duration", label: "Duration" },
                            ]
                      }
                      inputType="select"
                      isValidNewOption={() => false}
                      placeholder="Select input type"
                    />
                  </div>
                </div>
              )}

              {editedVariable?.Format === "short-text" &&
                editedVariable?.DataStructure === "Duration" && (
                  <div className="mb-3 d-flex gap-3">
                    <div className="flex-grow-1">
                      <label className="form-label">Duration units</label>
                      <Input
                        value={editedVariable?.Unit || []}
                        onChange={(newValue) =>
                          setEditedVariable((prev) => ({
                            ...prev,
                            Unit: newValue,
                          }))
                        }
                        isMulti
                        options={[
                          { value: "minute", label: "Minutes" },
                          { value: "hour", label: "Hours" },
                          { value: "day", label: "Days" },
                          { value: "week", label: "Weeks" },
                        ]}
                        inputType="select"
                        isValidNewOption={() => false}
                        placeholder="Select duration unit"
                      />
                    </div>
                  </div>
                )}

              {((["short-text", "number"].includes(editedVariable?.Format) &&
                editedVariable?.IsSelect) ||
                (["configuration"].includes(editedVariable?.Format) &&
                  editedVariable?.DataStructure === "Buttons")) && (
                <div className="mb-3">
                  <label className="form-label">Options</label>
                  <Input
                    inputType="select"
                    isMulti
                    isValidNewOption={(v) => {
                      if (editedVariable?.Format === "number" && isNaN(v)) {
                        return false;
                      }
                      return v.trim().length > 0;
                    }}
                    value={editedVariable?.SelectOptions || []}
                    onChange={(values) => {
                      setEditedVariable((prev) => ({
                        ...prev,
                        SelectOptions: values,
                        ...(usesButtonOptions
                          ? {
                              SelectOptionsIcons: getPrunedButtonOptionIcons(
                                values,
                                prev.SelectOptionsIcons
                              ),
                            }
                          : {}),
                      }));
                    }}
                    placeholder="Add options"
                    options={
                      editedVariable?.SelectOptions?.map((option) => ({
                        value: option,
                        label: option,
                      })) ?? []
                    }
                  />
                  {usesButtonOptions && (
                    <div className="mt-3 d-flex flex-column gap-2">
                      {(editedVariable?.SelectOptions || []).map(
                        (option, index) => {
                          const value = `${option ?? ""}`;
                          const icon = getButtonOptionIcon(
                            option,
                            editedVariable?.SelectOptionsIcons
                          );

                          if (!value) {
                            return null;
                          }

                          return (
                            <div key={`${value}-${index}`}>
                              <label className="form-label small mb-1">
                                {value} icon
                              </label>
                              <Input
                                inputType="select"
                                value={[icon || ""]}
                                onChange={([newIcon]) => {
                                  setEditedVariable((prev) => {
                                    const selectOptions =
                                      prev.SelectOptions || [];
                                    const optionIcons = {
                                      ...getPrunedButtonOptionIcons(
                                        selectOptions,
                                        prev.SelectOptionsIcons
                                      ),
                                    };

                                    if (newIcon) {
                                      optionIcons[value] = newIcon;
                                    } else {
                                      delete optionIcons[value];
                                    }

                                    return {
                                      ...prev,
                                      SelectOptions: selectOptions,
                                      SelectOptionsIcons: optionIcons,
                                    };
                                  });
                                }}
                                options={undefined}
                                loadOptions={loadSelectOptionIconOptions}
                                formatOptionLabel={
                                  formatSelectOptionIconOptionLabel
                                }
                                getValueLabels={getSelectOptionIconValueLabels}
                                isClearable
                                placeholder="Select icon"
                              />
                            </div>
                          );
                        }
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="row mb-3">
                <div>
                  <button
                    type="button"
                    className="btn btn-link float-end p-0"
                    onClick={() =>
                      setEditedVariable((prev) => ({
                        ...prev,
                        IsRequiredManual: true,
                        IsRequiredSMS: true,
                        IsRequiredEmail: true,
                        IsRequiredVoice: true,
                        IsRequiredHTTP: true,
                      }))
                    }
                  >
                    select all
                  </button>
                  <label className="form-label">Is required</label>
                </div>
                {/* Is required manual */}
                <div className="col-4">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="inputVariableIsRequiredManual"
                      checked={editedVariable?.IsRequiredManual ?? false}
                      onChange={() =>
                        setEditedVariable((prev) => ({
                          ...prev,
                          IsRequiredManual: !prev.IsRequiredManual,
                        }))
                      }
                      disabled={!editedVariable?.IsParameter}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="inputVariableIsRequiredManual"
                    >
                      in manual?
                    </label>
                  </div>
                </div>
                {/* Is required sms */}
                {!["binary"].includes(editedVariable?.Format) && (
                  <div className="col-4">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="inputVariableIsRequiredSMS"
                        checked={editedVariable?.IsRequiredSMS ?? false}
                        onChange={() =>
                          setEditedVariable((prev) => ({
                            ...prev,
                            IsRequiredSMS: !prev.IsRequiredSMS,
                          }))
                        }
                      />
                      <label
                        className="form-check-label"
                        htmlFor="inputVariableIsRequiredSMS"
                      >
                        in SMS?
                      </label>
                    </div>
                  </div>
                )}
                {/* Is required Email */}
                <div className="col-4">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="inputVariableIsRequiredEmail"
                      checked={editedVariable?.IsRequiredEmail ?? false}
                      onChange={() =>
                        setEditedVariable((prev) => ({
                          ...prev,
                          IsRequiredEmail: !prev.IsRequiredEmail,
                        }))
                      }
                    />
                    <label
                      className="form-check-label"
                      htmlFor="inputVariableIsRequiredEmail"
                    >
                      in email?
                    </label>
                  </div>
                </div>
                {/* Is required voice */}
                {!["binary"].includes(editedVariable?.Format) && (
                  <div className="col-4">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="inputVariableIsRequiredVoice"
                        checked={editedVariable?.IsRequiredVoice ?? false}
                        onChange={() =>
                          setEditedVariable((prev) => ({
                            ...prev,
                            IsRequiredVoice: !prev.IsRequiredVoice,
                          }))
                        }
                      />
                      <label
                        className="form-check-label"
                        htmlFor="inputVariableIsRequiredVoice"
                      >
                        in voice?
                      </label>
                    </div>
                  </div>
                )}
                {/* Is required HHTP */}
                <div className="col-4">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="inputVariableIsRequiredHTTP"
                      checked={editedVariable?.IsRequiredHTTP ?? false}
                      onChange={() =>
                        setEditedVariable((prev) => ({
                          ...prev,
                          IsRequiredHTTP: !prev.IsRequiredHTTP,
                        }))
                      }
                    />
                    <label
                      className="form-check-label"
                      htmlFor="inputVariableIsRequiredHTTP"
                    >
                      in HTTP?
                    </label>
                  </div>
                </div>
              </div>

              {/* Default value */}
              {(!editedVariable?.IsRequiredHTTP ||
                !editedVariable?.IsRequiredManual) &&
                !["binary", "asset", "event", "data-entry", "ticket"].includes(
                  editedVariable?.Format
                ) && (
                  <div className="mb-3">
                    <label className="form-label">Default value</label>
                    <InputArgumentField
                      arg={{ ...editedVariable, IsMultiple: false }}
                      values={defaultValueValues}
                      onChange={([v]) =>
                        setEditedVariable((prev) => ({
                          ...prev,
                          Default: v,
                        }))
                      }
                      inputArgumentsInEditor={inputArguments.filter(
                        (arg) =>
                          arg.InEditor && arg.UUID !== editedVariable?.UUID
                      )}
                    />
                  </div>
                )}

              {
                // Contact field
                ["text"].includes(editedVariable?.Format) && (
                  <>
                    <label htmlFor="inputTokenValue" className="form-label">
                      Match contact field
                    </label>
                    <select
                      className="form-select"
                      id="inputTokenValue"
                      value={editedVariable?.MatchContactField ?? ""}
                      onChange={(e) =>
                        setEditedVariable((prev) => ({
                          ...prev,
                          MatchContactField: e.target.value,
                        }))
                      }
                    >
                      <option value="">Do not match</option>
                      {contactFields?.map((cf) => (
                        <option key={cf.uuid} value={cf.uuid}>
                          {cf.label}
                        </option>
                      ))}
                    </select>
                  </>
                )
              }

              {/* Date time options */}
              {(editedVariable?.Format === "date" ||
                editedVariable?.Format === "time" ||
                editedVariable?.Format === "dateTime") && (
                <>
                  <div className="mb-3">
                    <label
                      htmlFor="inputVariableDateFormatString"
                      className="form-label"
                    >
                      Default date format
                    </label>
                    <input
                      type="text"
                      placeholder="Date format string"
                      className="form-control"
                      id="inputVariableDateFormatString"
                      value={editedVariable?.DateTimeFormat}
                      onChange={(e) =>
                        setEditedVariable((prev) => ({
                          ...prev,
                          DateTimeFormat: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Default time zone</label>
                    <Input
                      inputType="select"
                      isValidNewOption={() => false}
                      options={[
                        {
                          value: "",
                          label: projectTimezone
                            ? `Project default (${projectTimezone})`
                            : `Product default through project (${pageProps.defaultTZ})`,
                        },
                        ...timezones.map((tz) => ({
                          value: tz.tzCode,
                          label: tz.label,
                        })),
                      ]}
                      value={[editedVariable?.DefaultTimeZone ?? ""]}
                      onChange={(values) =>
                        setEditedVariable((prev) => ({
                          ...prev,
                          DefaultTimeZone: values[0],
                        }))
                      }
                    />
                  </div>
                </>
              )}

              {!["editor", "group"].includes(editedVariable?.Format) &&
                (["binary"].includes(editedVariable?.Format) ? (
                  <>
                    <h5 className="mt-5">Email settings</h5>
                    <div className="mb-3" key={editedVariable?.Format}>
                      <Input
                        inputType="select"
                        options={[
                          {
                            value: "",
                            label: "Do not extract",
                          },
                          {
                            value: "all-attachments",
                            label: "Extract all attachments",
                          },
                          {
                            value: "match-attachments",
                            label:
                              "Extract attachments with filename containing...",
                          },
                        ]}
                        value={[editedVariable?.Extract || ""]}
                        onChange={(values) =>
                          setEditedVariable((prev) => ({
                            ...prev,
                            Extract: values[0],
                            // give IsCopy default true if not set yet
                            IsCopy: editedVariable?.IsCopy ?? true,
                          }))
                        }
                      />
                    </div>
                    {
                      // show copy directly option
                      editedVariable?.Extract === "match-attachments" && (
                        <div className="mb-3">
                          <label
                            htmlFor="inputVariableFilenameMatch"
                            className="form-label"
                          >
                            String match for filename
                          </label>
                          <input
                            type="text"
                            placeholder="Match filename containing..."
                            className="form-control"
                            id="inputVariableFilenameMatch"
                            value={editedVariable?.AttachmentContains ?? ""}
                            onChange={(e) =>
                              setEditedVariable((prev) => ({
                                ...prev,
                                AttachmentContains: e.target.value,
                              }))
                            }
                          />
                        </div>
                      )
                    }
                  </>
                ) : (
                  <>
                    <div className="mb-3" id="email_SMS_settings">
                      <label className="form-label mt-5">
                        Email/SMS settings
                      </label>
                      <Input
                        inputType="select"
                        options={[
                          {
                            value: "",
                            label: "Do not extract",
                          },
                          {
                            value: "EmailOrSmsBody",
                            label: "Extract from email or SMS body",
                          },
                          {
                            value: "EmailSubject",
                            label: "Extract from email subject",
                          },
                        ]}
                        value={[editedVariable?.Extract || ""]}
                        onChange={(values) =>
                          setEditedVariable((prev) => ({
                            ...prev,
                            Extract: values[0],
                            // give IsCopy default true if not set yet
                            IsCopy: editedVariable?.IsCopy ?? true,
                          }))
                        }
                      />
                    </div>

                    {
                      // show copy directly option
                      editedVariable?.Extract && (
                        <div className="mb-3 form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="inputVariableIsCopy"
                            checked={editedVariable?.IsCopy || false}
                            onChange={() =>
                              setEditedVariable((prev) => ({
                                ...prev,
                                IsCopy: !prev.IsCopy,
                              }))
                            }
                          />
                          <label
                            className="form-check-label"
                            htmlFor="inputVariableIsCopy"
                          >
                            Copy directly from source without extraction
                          </label>
                        </div>
                      )
                    }
                    {
                      // show extraction options
                      editedVariable?.IsCopy === false &&
                        editedVariable?.Extract && (
                          <ExtractOptions
                            options={editedVariable?.ExtractOptions || {}}
                            onChange={(options) =>
                              setEditedVariable((prev) => ({
                                ...prev,
                                ExtractOptions: options,
                              }))
                            }
                          />
                        )
                    }
                  </>
                ))}
              <div className="mb-3">
                <label htmlFor="inputVariableOrder" className="form-label">
                  Order
                </label>
                <input
                  placeholder="Variable order"
                  className="form-control"
                  id="inputVariableOrder"
                  type="number"
                  value={editedVariable?.Order ?? ""}
                  onChange={(e) =>
                    setEditedVariable((prev) => ({
                      ...prev,
                      Order: parseInt(e.target.value, 10),
                    }))
                  }
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Variable group</label>
                <Input
                  inputType="select"
                  isValidNewOption={() => false}
                  options={variableGroups.map((vg) => ({
                    value: vg.GroupUuid,
                    label: vg.Label,
                  }))}
                  value={[editedVariable?.GroupUuid ?? ""]}
                  onChange={(values) =>
                    setEditedVariable((prev) => ({
                      ...prev,
                      GroupUuid: values[0],
                    }))
                  }
                />
              </div>
              <VisibilityInputs
                editedVariable={editedVariable}
                setEditedVariable={setEditedVariable}
              />
            </div>
            <UsedInLink arg={editedVariable} />
            <JsonCollapse json={editedVariable} />
          </Offcanvas.Body>
        </Offcanvas>
      </div>
    )
  );
}
